/**
 * The visit's **Equipment** tab — `docs/visit-page.md` §10 (Board 5), §27.5
 * (Board 18 G, H) and §27.4 (Board 18 I); the phone and iPad as §19 says
 * (lead decisions, round 1 item 7).
 *
 * Two sections, in the board's order:
 *
 * - **Company equipment** — *planned by type from Q-2184 · a unit is picked
 *   for each day* · **Add equipment**: a row per planned type (the loader
 *   tile, the type, *company · by type*), a cell per day — the unit's tag
 *   (green on today) and what it does that day, or the dashed **Any wet saw ·
 *   picked at 6:30 · 2 free** (*Decide in the morning*), or *— not needed*;
 *   then the hours and the estimate (internal, tinted). A click on a day still
 *   to come opens the unit picker (Board 5 B). A type with no units yet draws
 *   **Any mini skid steer · no units yet · 8 h** and **＋ Add a unit**
 *   (Board 18 G, H).
 * - **Rented from a vendor · 2** — *where to get it, who picks it up, when it
 *   goes back* · **Rent from a vendor**: a card per rental (`RentalCard`).
 *
 * `GET /visits/:id/equipment`; every write answers this tab's read again
 * (§5.2). `?rent=1` opens **Rent from a vendor** empty (the phone's **Rent**),
 * `?rent=<rental id>` on that rental (lead decisions, rounds 1 and 5).
 *
 * On a phone it is cards, never a table (§19.2): a card per section, a row
 * per type with a 44px row per day. On an iPad the desktop table, in the
 * narrower column — the tab has no rail (it is `wide`).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type {
  DayRef, EquipmentCell, EquipmentRead, EquipmentTypeRef, EquipmentTypeRow, RentalCard as RentalCardData, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { COMMON, EQUIPMENT, PHONE, RENTALS } from './copy.js'
import { dayText, hours1, hoursBare, localTime, money } from './format.js'
import { VisitGlyph } from './glyphs.js'
import { CountPill, HeadButton, KindTile } from './equipment-parts.js'
import { InternalChip } from './internal-chip.js'
import { RentalCard } from './rental-card.js'
import { UnitPicker, type PickingCell } from './unit-picker.js'
import { AddUnitPopup } from './add-unit.js'
import { AddEquipmentPopup } from './add-equipment.js'
import { CancelBookingPopup } from './cancel-booking.js'
import { RentSheet } from './rent-sheet.js'

export interface EquipmentTabProps {
  visitId: string
  read: VisitRead
  phone: boolean
}

/** How wide the tab's own box is — the panel, never the window (§19.1). A callback ref, so it starts when the box does. */
function useWidth(): [(node: HTMLDivElement | null) => void, number] {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    if (!node) return undefined
    const measure = () => setWidth(node.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const watcher = new ResizeObserver(measure)
    watcher.observe(node)
    return () => watcher.disconnect()
  }, [node])
  return [setNode, width]
}

/** Board 5's columns — `1.25fr 1.15fr 1.15fr 1.35fr 128px 124px` for three days; the last day is the wide one. */
function columns(days: number, money: boolean): string {
  const dayColumns = days <= 1 ? ['1.35fr'] : [...Array.from({ length: days - 1 }, () => '1.15fr'), '1.35fr']
  return ['1.25fr', ...dayColumns, '128px', ...(money ? ['124px'] : [])].join(' ')
}

/** *Day 2 · Thu Oct 22 · today* — only today is called out (Board 5's head). */
function dayHead(day: DayRef): string {
  const date = dayText(day.date, 'date')
  return day.relative === 'today'
    ? COMMON.dayDateRelative(String(day.index), date, COMMON.today)
    : COMMON.dayDate(String(day.index), date)
}

/** A day still to come may have its unit picked; a day done has its hours. */
function pickable(cell: EquipmentCell): boolean {
  return cell.kind === 'decide' || (cell.kind === 'unit' && cell.usedHours === null)
}

/** The unit's tag — 24px, radius 7, 12.5/700; green-tinted on today. */
function UnitTag({ tag, today }: { tag: string; today: boolean }) {
  return (
    <span className={cn('inline-flex h-[26px] w-max items-center whitespace-nowrap rounded-tile-small border px-2 text-[12.5px] font-bold text-text',
      today ? 'border-accent-line bg-green-band-bg' : 'border-line bg-surface')}>
      {tag}
    </span>
  )
}

/** The dashed box — *Any wet saw · picked at 6:30 · 2 free* · *Any mini skid steer · no units yet · 8 h*. */
function Dashed({ title, sub, nowrap }: { title: string; sub: string; nowrap: boolean }) {
  return (
    <span className="inline-flex flex-col gap-0.5 rounded-control border-[1.5px] border-dashed border-line-strong px-2.5 py-1.5 text-left">
      <span className={cn('text-[12.5px] font-semibold text-text', nowrap && 'whitespace-nowrap')}>{title}</span>
      <span className={cn('text-[11px] text-text-muted', nowrap && 'whitespace-nowrap')}>{sub}</span>
    </span>
  )
}

/** What a day's cell draws. */
function CellBody({ cell, type, day }: { cell: EquipmentCell; type: EquipmentTypeRef; day: DayRef | undefined }) {
  switch (cell.kind) {
    case 'not_needed':
      return <span className="text-[12.5px] text-text-faint">{EQUIPMENT.cell.notNeeded}</span>
    case 'decide':
      return <Dashed nowrap={false} title={EQUIPMENT.cell.any(type.noun)} sub={EQUIPMENT.cell.picked(localTime(cell.pickAt), String(cell.free))} />
    case 'no_units':
      return <Dashed nowrap title={EQUIPMENT.cell.any(type.noun)} sub={EQUIPMENT.cell.noUnits(hoursBare(cell.plannedHours))} />
    case 'unit': {
      const planned = cell.plannedHours === null ? '' : hoursBare(cell.plannedHours)
      const line = cell.usedHours !== null ? EQUIPMENT.cell.used(hours1(cell.usedHours))
        : cell.where?.kind === 'on_site' ? EQUIPMENT.cell.onSite(planned)
          : cell.where?.kind === 'stays_on_site' ? EQUIPMENT.cell.staysOnSite(planned)
            : cell.where?.kind === 'hauled' ? EQUIPMENT.cell.hauled(cell.where.noun, planned)
              : cell.plannedHours !== null ? EQUIPMENT.cell.planned(planned) : null
      return (
        <span className="flex flex-col text-left">
          <UnitTag tag={cell.unit.tag} today={day?.relative === 'today'} />
          {line && <span className="mt-[3px] text-[11.5px] text-text-muted">{line}</span>}
        </span>
      )
    }
  }
}

/** The type's own cell: the loader tile, the name, and *company · by type* — or **＋ Add a unit** (Board 18 G). */
function TypeName({ row, canEdit, onAddUnit }: { row: EquipmentTypeRow; canEdit: boolean; onAddUnit: () => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <KindTile kind="equipment" glyph="loader" size={30} />
      <div className="flex min-w-0 flex-col">
        <b className="truncate text-[13px]">{row.type.name}</b>
        {row.noUnits && canEdit
          ? (
            <button type="button" onClick={onAddUnit}
              className="inline-flex w-max items-center gap-1 text-[11.5px] font-semibold text-accent hover:text-accent-dark">
              <VisitGlyph name="plus" size={11} stroke={2} />
              {EQUIPMENT.addUnit}
            </button>
          )
          : <span className="text-[11.5px] text-text-muted">{EQUIPMENT.byType}</span>}
      </div>
    </div>
  )
}

function TypeTable({ data, read, onPick, onAddUnit }: {
  data: EquipmentRead
  read: VisitRead
  onPick: (row: EquipmentTypeRow, day: DayRef, anchor: HTMLElement) => void
  onAddUnit: (type: EquipmentTypeRef) => void
}) {
  const withMoney = Boolean(data.internal)
  const template = columns(data.days.length, withMoney)
  const noUnits = data.footer.anyWithoutUnits
  return (
    <div
      {...(noUnits ? { 'data-as-drawn': 'vp-18 G' } : {})}
      className={cn('min-w-0 rounded-record border border-line bg-surface', noUnits && 'overflow-hidden')}
    >
      <div className="overflow-x-auto">
        <div className="min-w-[960px]">
          <div
            role="row"
            className="grid h-[35px] items-center gap-3 rounded-t-record border-b border-line bg-surface-alt px-[18px] text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint"
            style={{ gridTemplateColumns: template }}
          >
            <span className="whitespace-nowrap">{EQUIPMENT.head.type}</span>
            {data.days.map((day) => <span key={day.index} className="whitespace-nowrap">{dayHead(day)}</span>)}
            <span className="whitespace-nowrap">{EQUIPMENT.head.hours}</span>
            {withMoney && <span className="whitespace-nowrap text-right">{EQUIPMENT.head.estCost}</span>}
          </div>

          {data.types.map((row) => (
            <div
              key={row.id}
              role="row"
              className="grid items-stretch gap-3 border-b border-line-faint px-[18px] py-2 text-[13px]"
              style={{ gridTemplateColumns: template, minHeight: row.noUnits ? 66 : 62 }}
            >
              <div className="flex min-w-0 items-center">
                <TypeName row={row} canEdit={read.can.edit} onAddUnit={() => onAddUnit(row.type)} />
              </div>
              {row.cells.map((cell) => {
                const day = data.days.find((one) => one.index === cell.day)
                const body = <CellBody cell={cell} type={row.type} day={day} />
                return (
                  <div key={cell.day} className="flex min-w-0 items-center">
                    {read.can.edit && day && pickable(cell)
                      ? (
                        <button type="button" aria-haspopup="dialog" className="rounded-control text-left"
                          onClick={(event) => onPick(row, day, event.currentTarget)}>
                          {body}
                        </button>
                      )
                      : body}
                  </div>
                )
              })}
              <div className="flex min-w-0 items-center">
                <div className="flex flex-col">
                  <b className="text-[13px]">{EQUIPMENT.hoursPlanned(hoursBare(row.hours.planned))}</b>
                  <span className="text-[11.5px] text-text-muted">
                    {row.hours.used > 0 ? EQUIPMENT.hoursUsed(hours1(row.hours.used)) : EQUIPMENT.noneUsed}
                  </span>
                </div>
              </div>
              {withMoney && (
                <div className="flex min-w-0 items-center justify-end text-right">
                  <div className="-my-2 flex flex-1 flex-col items-end justify-center self-stretch border-l border-line-soft bg-surface-internal px-2.5 py-2">
                    {row.internal && (
                      <>
                        <span className="text-[13px]">{money(row.internal.estimate)}</span>
                        <span className="text-[11px] text-text-faint">{EQUIPMENT.perHour(money(row.internal.rate))}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {noUnits
            ? (
              <div className="border-t border-line-faint bg-surface-alt px-[18px] py-2.5 text-[12px] text-text-muted">
                {EQUIPMENT.noUnitsFooter}
              </div>
            )
            : (
              <div
                className="grid h-[46px] items-stretch gap-3 rounded-b-record bg-surface-alt px-[18px] text-[13px]"
                style={{ gridTemplateColumns: template }}
              >
                <div className="flex items-center"><b>{EQUIPMENT.footer(data.footer.types)}</b></div>
                <div className="flex items-center text-[12px] text-text-muted" style={{ gridColumn: `2 / ${2 + data.days.length}` }}>
                  {data.internal ? EQUIPMENT.footerSub(money(data.internal.soFar)) : EQUIPMENT.footerSubHours}
                </div>
                <div className="flex items-center text-[12.5px] font-semibold">
                  {EQUIPMENT.footerHours(hoursBare(data.footer.plannedHours), hoursBare(data.footer.usedHours))}
                </div>
                {withMoney && data.internal && (
                  <div className="flex items-center justify-end border-l border-line-soft bg-surface-internal px-2.5">
                    <b>{money(data.internal.estimate)}</b>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

/** A phone: the types as a card of rows, a 44px row per day (§19.2 — cards, never a table). */
function TypeCards({ data, read, onPick, onAddUnit, onAdd, header = true }: {
  data: EquipmentRead
  read: VisitRead
  onPick: (row: EquipmentTypeRow, day: DayRef, anchor: HTMLElement) => void
  onAddUnit: (type: EquipmentTypeRef) => void
  onAdd: () => void
  /** The card's own head — the phone's; an iPad keeps the tab's section head above it. */
  header?: boolean
}) {
  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface">
      {header && (
      <header className="flex items-center gap-2 px-3.5 py-[11px]">
        <b className="text-[13.5px]">{EQUIPMENT.title}</b>
        <span className="text-[13px] text-text-muted">{COMMON.count(String(data.footer.types))}</span>
        <span className="flex-1" />
        {data.canAdd && read.can.edit && (
          <button type="button" onClick={onAdd}
            className="-my-2 inline-flex h-11 items-center gap-1 px-1 text-[12.5px] font-semibold text-accent">
            <VisitGlyph name="plus" size={13} stroke={2} />
            {PHONE.add}
          </button>
        )}
      </header>
      )}
      {header && data.quoteReference && (
        <p className="m-0 -mt-1.5 px-3.5 pb-2.5 text-[12px] text-text-muted">{EQUIPMENT.sub(data.quoteReference)}</p>
      )}
      {data.types.map((row, at) => (
        <div key={row.id} className={cn('px-3.5 py-3', (header || at > 0) && 'border-t border-line-faint')}>
          <div className="flex items-start gap-2.5">
            <div className="min-w-0 flex-1">
              <TypeName row={row} canEdit={read.can.edit} onAddUnit={() => onAddUnit(row.type)} />
            </div>
            <div className="flex shrink-0 flex-col items-end">
              <b className="text-[13px]">{EQUIPMENT.hoursPlanned(hoursBare(row.hours.planned))}</b>
              <span className="text-[12px] text-text-muted">
                {row.hours.used > 0 ? EQUIPMENT.hoursUsed(hours1(row.hours.used)) : EQUIPMENT.noneUsed}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-col">
            {row.cells.map((cell) => {
              const day = data.days.find((one) => one.index === cell.day)
              /* *Day 2 · Thu · today* — the date's weekday alone, so the label holds one line at 390. */
              const label = !day ? COMMON.dayN(String(cell.day))
                : day.relative === 'today'
                  ? COMMON.dayDateRelative(String(day.index), dayText(day.date, 'day'), COMMON.today)
                  : COMMON.dayDate(String(day.index), dayText(day.date, 'day'))
              const inner = (
                <>
                  <span className="w-[112px] shrink-0 text-[12px] font-semibold text-text-muted">{label}</span>
                  <span className="min-w-0 flex-1"><CellBody cell={cell} type={row.type} day={day} /></span>
                </>
              )
              return read.can.edit && day && pickable(cell)
                ? (
                  <button key={cell.day} type="button" aria-haspopup="dialog"
                    onClick={(event) => onPick(row, day, event.currentTarget)}
                    className="flex min-h-11 items-center gap-2.5 border-t border-line-faint py-1.5 text-left first:border-t-0">
                    {inner}
                    <VisitGlyph name="chevronRight" size={14} stroke={2} />
                  </button>
                )
                : (
                  <div key={cell.day} className="flex min-h-11 items-center gap-2.5 border-t border-line-faint py-1.5 first:border-t-0">
                    {inner}
                  </div>
                )
            })}
          </div>
          {row.internal && (
            <div className="mt-2 flex items-center gap-2 rounded-card border border-line bg-surface-internal px-3 py-2 text-[12.5px]">
              <InternalChip />
              <span className="text-text-muted">{EQUIPMENT.perHour(money(row.internal.rate))}</span>
              <span className="flex-1" />
              <b>{money(row.internal.estimate)}</b>
            </div>
          )}
        </div>
      ))}
      <footer className="flex flex-col gap-0.5 border-t border-line-faint bg-surface-alt px-3.5 py-2.5">
        {data.footer.anyWithoutUnits
          ? <span className="text-[12px] text-text-muted">{EQUIPMENT.noUnitsFooter}</span>
          : (
            <>
              <span className="flex items-center gap-2 text-[13px]">
                <b>{EQUIPMENT.footer(data.footer.types)}</b>
                <span className="font-semibold">
                  {EQUIPMENT.footerHours(hoursBare(data.footer.plannedHours), hoursBare(data.footer.usedHours))}
                </span>
                <span className="flex-1" />
                {data.internal && <b>{money(data.internal.estimate)}</b>}
              </span>
              <span className="text-[12px] text-text-muted">
                {data.internal ? EQUIPMENT.footerSub(money(data.internal.soFar)) : EQUIPMENT.footerSubHours}
              </span>
            </>
          )}
      </footer>
    </section>
  )
}

function SectionHead({ title, count, sub, button, phone }: {
  title: string
  count?: number
  sub: string | null
  button: ReactNode
  phone: boolean
}) {
  if (phone) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2.5">
          <b className="text-[15px]">{title}</b>
          {count !== undefined && count > 0 && <CountPill n={count} />}
          <span className="flex-1" />
          {button}
        </div>
        {sub && <span className="text-[12.5px] text-text-muted">{sub}</span>}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2.5">
      <b className="shrink-0 text-[15px]">{title}</b>
      {count !== undefined && count > 0 && <CountPill n={count} />}
      {sub && <span className="min-w-0 truncate text-[12.5px] text-text-muted">{sub}</span>}
      <span className="flex-1" />
      {button}
    </div>
  )
}

export function EquipmentTab({ visitId, read, phone }: EquipmentTabProps) {
  const { data, refresh, error } = useVisitTab<EquipmentRead>(visitId, '/equipment')
  const write = useVisitWrite(visitId)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [box, width] = useWidth()
  const [picking, setPicking] = useState<PickingCell | null>(null)
  const [addingUnit, setAddingUnit] = useState<EquipmentTypeRef | null>(null)
  const [adding, setAdding] = useState(false)
  const [cancelling, setCancelling] = useState<RentalCardData | null>(null)
  const [marking, setMarking] = useState<string | null>(null)
  const [markError, setMarkError] = useState<{ id: string; message: string } | null>(null)

  /* `?rent=1` — Rent from a vendor, empty; `?rent=<rental id>` — Book it / Change on that rental. */
  const rent = params.get('rent')
  const setRent = (value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set('rent', value)
    else next.delete('rent')
    setParams(next, { replace: value === null })
  }

  const written = (next: EquipmentRead) => refresh(next)

  function addUnit(type: EquipmentTypeRef) {
    /* With the module's units on, its own unit form takes it (§27.5). */
    if (read.capabilities['equipment.units']) { navigate('/equipment'); return }
    setAddingUnit(type)
  }

  async function mark(card: RentalCardData, stop: 'pick_up' | 'return') {
    setMarking(card.id)
    setMarkError(null)
    try {
      const result = await write('POST', `/rentals/${card.id}/${stop === 'pick_up' ? 'picked-up' : 'returned'}`,
        { version: card.version }, { tab: 'equipment' })
      if (result.tab) refresh(result.tab as EquipmentRead)
    } catch (cause) {
      setMarkError({ id: card.id, message: cause instanceof Error ? cause.message : String(cause) })
    } finally {
      setMarking(null)
    }
  }

  if (!data) {
    return error ? <p className="m-0 text-[12.5px] text-danger">{error}</p> : null
  }

  const twoUp = !phone && width >= 900
  /*
   * The table needs 960 (its columns, Board 5 A). Narrower — an iPad held
   * upright — it would scroll sideways and hide Hours and Est. cost, so the
   * types are the phone's cards under the tab's own head (no board draws the
   * tab on an iPad: a DEVIATION REQUEST).
   */
  const cards = phone || (width > 0 && width < 960)
  return (
    <div ref={box} className="flex min-w-0 flex-col gap-4 leading-[normal] tabular-nums">
      {!phone && (
        <SectionHead
          phone={false}
          title={EQUIPMENT.title}
          sub={data.quoteReference ? EQUIPMENT.sub(data.quoteReference) : null}
          button={data.canAdd && read.can.edit
            ? <HeadButton phone={false} onClick={() => setAdding(true)}>{EQUIPMENT.add}</HeadButton>
            : null}
        />
      )}
      {cards
        ? (
          (phone ? data.types.length > 0 || data.canAdd : data.types.length > 0)
            ? <TypeCards data={data} read={read} onAddUnit={addUnit} onAdd={() => setAdding(true)} header={phone}
              onPick={(row, day, anchor) => setPicking({ typeId: row.type.id, typeName: row.type.name, day, anchor })} />
            : null
        )
        : data.types.length > 0 && (
          <TypeTable data={data} read={read} onAddUnit={addUnit}
            onPick={(row, day, anchor) => setPicking({ typeId: row.type.id, typeName: row.type.name, day, anchor })} />
        )}

      <div className={cn(!phone && 'mt-2')}>
        <SectionHead
          phone={phone}
          title={RENTALS.title}
          count={data.rentals.length}
          sub={RENTALS.sub}
          /* A phone's way in is the quick action **Rent** (§19.2: one green action per tab). */
          button={!phone && data.canRent && read.can.edit
            ? <HeadButton primary phone={false} onClick={() => setRent('1')}>{RENTALS.add}</HeadButton>
            : null}
        />
      </div>
      {data.rentals.length > 0 && (
        <div className={cn('grid items-stretch gap-4', twoUp ? 'grid-cols-2' : 'grid-cols-1')}>
          {data.rentals.map((card) => (
            <div key={card.id} className="flex min-w-0 flex-col gap-1.5">
              <RentalCard
                card={card}
                read={read}
                phone={phone}
                busy={marking === card.id}
                onBook={() => setRent(card.id)}
                onChange={() => setRent(card.id)}
                onCancel={() => setCancelling(card)}
                onMark={(stop) => void mark(card, stop)}
              />
              {markError?.id === card.id && <p className="m-0 text-[12.5px] text-danger">{markError.message}</p>}
            </div>
          ))}
        </div>
      )}

      {picking && (
        <UnitPicker visitId={visitId} read={read} cell={picking} phone={phone}
          onClose={() => setPicking(null)} onWritten={written} />
      )}
      {addingUnit && (
        <AddUnitPopup visitId={visitId} read={read} type={addingUnit} phone={phone}
          onClose={() => setAddingUnit(null)} onWritten={written} />
      )}
      {adding && (
        <AddEquipmentPopup visitId={visitId} read={read} phone={phone}
          onClose={() => setAdding(false)} onWritten={written} />
      )}
      {cancelling && (
        <CancelBookingPopup visitId={visitId} rental={cancelling} phone={phone}
          onClose={() => setCancelling(null)} onWritten={written} />
      )}
      {rent && (
        <RentSheet
          visitId={visitId}
          read={read}
          rentalId={rent === '1' ? null : rent}
          tab="equipment"
          phone={phone}
          onClose={() => setRent(null)}
          onWritten={(result) => { if (result.tab) refresh(result.tab as EquipmentRead) }}
        />
      )}
    </div>
  )
}
